import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  resolveTaxContractFromProfile,
  resolveTaxLabelFromProfile,
} from "../utils/taxDetails";

import { API_BASE } from "../config/constants";
import { cacheProducts, getCachedProducts } from "../utils/productListCache";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import { listOfflineCategories } from "../offline/categoryStore";
import {
  bulkUpdateOfflineProductTax,
  listOfflineProducts,
} from "../offline/productStore";
const BRAND = "var(--app-primary)";
const BRAND_DARK = "#014a7d";
const BRAND_SOFT = "var(--app-primary-soft)";

const GST_OPTIONS = ["0", "5", "12", "18", "28"];

const toApiTaxChoice = (choice = "") =>
  String(choice || "").toLowerCase() === "with" ? "withgst" : "withoutgst";

const productIdentity = (product) => String(
  typeof product === "object"
    ? product?.id ?? product?.productid ?? product?.product_id ?? ""
    : product ?? ""
).trim();

const BulkGstUpdatePage = () => {
  const [gstFilter, setGstFilter] = useState("all");
  const [userData, setUserData] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [gstPercent, setGstPercent] = useState("");
  const [taxChoice, setTaxChoice] = useState("");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const taxLabel = resolveTaxLabelFromProfile(userData || {});
  const genericTax = resolveTaxContractFromProfile(userData || {}) === "generic_tax_v2";
  const taxOptions = [
    { value: "with", label: `With ${taxLabel}` },
    { value: "without", label: `Without ${taxLabel}` },
  ];

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const filteredProducts = useMemo(() => {
    if (gstFilter === "applied") return products.filter((item) => isGstApplied(item));
    if (gstFilter === "not_applied") return products.filter((item) => !isGstApplied(item));
    return products;
  }, [gstFilter, products]);
  const allSelected =
    filteredProducts.length > 0 &&
    filteredProducts.every((item) => selectedIdSet.has(productIdentity(item)));

  const fetchProducts = useCallback(async (restaurantId, useCache = true) => {
    if (!restaurantId) {
      setProducts([]);
      setSelectedIds([]);
      return;
    }
    if (isFullOfflineModeEnabled()) {
      const localProducts = await listOfflineProducts(restaurantId);
      const sortedLocal = sortProducts(localProducts);
      const idSet = new Set(sortedLocal.map(productIdentity).filter(Boolean));
      setProducts(sortedLocal);
      setSelectedIds((prev) => prev.filter((id) => idSet.has(String(id))));
      return;
    }
    const cachedProducts = useCache
      ? await getCachedProducts(restaurantId, "master")
      : null;
    if (cachedProducts?.length) {
      const sortedCached = sortProducts(cachedProducts);
      setProducts(sortedCached);
      setSelectedIds((prev) => prev.filter((id) =>
        sortedCached.some((item) => productIdentity(item) === String(id))));
    }
    const response = await fetch(`${API_BASE}/product/list/master`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ restaurantid: restaurantId }),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result?.message || "Failed to fetch products");
    const list = Array.isArray(result?.list) ? result.list : [];
    cacheProducts(restaurantId, list, "master");
    const sorted = sortProducts(list);
    const idSet = new Set(
      sorted
        .map(productIdentity)
        .filter(Boolean)
    );
    setProducts(sorted);
    setSelectedIds((prev) => prev.filter((id) => idSet.has(id)));
  }, []);

  const fetchCategories = useCallback(async (restaurantId) => {
    if (!restaurantId) {
      setCategories([]);
      return;
    }
    if (isFullOfflineModeEnabled()) {
      setCategories(await listOfflineCategories(restaurantId));
      return;
    }
    const response = await fetch(`${API_BASE}/list/productcategory`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ restaurantid: restaurantId }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result?.message || "Failed to fetch categories");
    const list = Array.isArray(result?.list) ? result.list : [];
    setCategories(list);
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const stored = JSON.parse(localStorage.getItem("userData") || "{}");
        if (stored?.data) {
          setUserData(stored.data);
          if (stored.data.id) {
            await Promise.all([
              fetchProducts(stored.data.id),
              fetchCategories(stored.data.id),
            ]);
          }
        }
      } catch (e) {
        toast.error(e?.message || "Unable to load products");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [fetchCategories, fetchProducts]);

  const toggleSelectAll = () => {
    if (!filteredProducts.length) return;
    if (allSelected) {
      const filteredIdSet = new Set(
        filteredProducts
          .map(productIdentity)
          .filter(Boolean)
      );
      setSelectedIds((prev) => prev.filter((id) => !filteredIdSet.has(id)));
      return;
    }
    const ids = filteredProducts
      .map(productIdentity)
      .filter(Boolean);
    setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const toggleProduct = (id) => {
    const productId = productIdentity(id);
    if (!productId) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return Array.from(next);
    });
  };

  const validateInputs = () => {
    if (!selectedIds.length) {
      toast.error("Select products to update.");
      return false;
    }
    if (!gstPercent) {
      toast.error(`Enter ${taxLabel} percent.`);
      return false;
    }
    if (!taxChoice) {
      toast.error("Select tax option.");
      return false;
    }
    if (!userData?.id) {
      toast.error("Restaurant details missing.");
      return false;
    }
    return true;
  };

  const submitUpdate = async () => {
    try {
      setUpdating(true);
      const numericIds = selectedIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));
      const apiTaxChoice = toApiTaxChoice(taxChoice);
      if (isFullOfflineModeEnabled()) {
        const result = await bulkUpdateOfflineProductTax(
          userData.id,
          selectedIds,
          Number(gstPercent || 0),
          apiTaxChoice,
          genericTax
        );
        const count = Number(result?.updated_products ?? selectedIds.length ?? 0);
        toast.success(`Updated ${count} products.`);
        setLoading(true);
        await fetchProducts(userData.id, false);
        setSelectedIds([]);
        setGstPercent("");
        setTaxChoice("");
        return;
      }
      const payload = {
        restaurantid: userData.id,
        gstpercetage: Number(gstPercent || 0),
        withorwithoutgst: apiTaxChoice,
        gstenable: Number(gstPercent || 0) > 0 ? 1 : 0,
        productids: numericIds,
        product_ids: numericIds,
        products: numericIds,
      };
      const response = await fetch(`${API_BASE}/product/bulk-gst`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result?.success === false) {
        const fieldError = result?.errors?.withorwithoutgst?.[0];
        throw new Error(fieldError || result?.message || "Update failed");
      }
      const count = Number(result?.updated_products ?? numericIds.length ?? 0);
      toast.success(`Updated ${count} products.`);
      setLoading(true);
      await fetchProducts(userData.id, false);
      setSelectedIds([]);
      setGstPercent("");
      setTaxChoice("");
    } catch (e) {
      toast.error(e?.message || "Update failed. Try again.");
    } finally {
      setLoading(false);
      setUpdating(false);
    }
  };

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <Hero>
        <div>
          <Badge>Bulk {taxLabel}</Badge>
          <Title>Bulk {taxLabel} Update</Title>
          <Sub>Apply {taxLabel} settings to multiple items at once.</Sub>
        </div>
      </Hero>

      <Controls>
        <ControlBlock>
          <label>{taxLabel} Percent</label>
          {genericTax ? (
            <input
              value={gstPercent}
              onChange={(e) => setGstPercent(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder={`Enter ${taxLabel} %`}
              inputMode="decimal"
            />
          ) : <select value={gstPercent} onChange={(e) => setGstPercent(e.target.value)}>
            <option value="">Select</option>
            {GST_OPTIONS.map((g) => (
              <option key={g} value={g}>
                {g}%
              </option>
            ))}
          </select>}
        </ControlBlock>
        <ControlBlock>
          <label>Tax Option</label>
          <select value={taxChoice} onChange={(e) => setTaxChoice(e.target.value)}>
            <option value="">Select</option>
            {taxOptions.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </ControlBlock>
        <PrimaryBtn disabled={updating} onClick={() => validateInputs() && submitUpdate()}>
          {updating ? "Updating..." : "Apply"}
        </PrimaryBtn>
      </Controls>

      <Toolbar>
        <FilterGroup>
          <FilterBtn
            type="button"
            $active={gstFilter === "all"}
            onClick={() => setGstFilter("all")}
          >
            All
          </FilterBtn>
          <FilterBtn
            type="button"
            $active={gstFilter === "applied"}
            onClick={() => setGstFilter("applied")}
          >
            {taxLabel} Applied
          </FilterBtn>
          <FilterBtn
            type="button"
            $active={gstFilter === "not_applied"}
            onClick={() => setGstFilter("not_applied")}
          >
            {taxLabel} Not Applied
          </FilterBtn>
        </FilterGroup>
        <div>
          <strong>{selectedIds.length}</strong> selected
        </div>
        <button type="button" onClick={toggleSelectAll}>
          {allSelected ? "Clear All" : "Select All"}
        </button>
      </Toolbar>

      <Card>
        {loading ? (
          <Center>Loading…</Center>
        ) : filteredProducts.length === 0 ? (
          <Center>No products found.</Center>
        ) : (
          <List>
            {filteredProducts.map((item) => {
              const id = productIdentity(item);
              const selected = selectedIdSet.has(id);
              const gstPercent = pickGstPercent(item);
              const gstApplied = isGstApplied(item);
              const taxType = pickTaxTypeLabel(item);
              const category = pickCategoryLabel(item, categories);
              return (
                <Row key={id || item?.name} $selected={selected}>
                  <Left>
                    <label>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleProduct(id)}
                      />
                      <span>{item?.name}</span>
                    </label>
                    <MetaLine>
                      <small>{category}</small>
                      <Dot>•</Dot>
                      <small>{gstApplied ? `${taxLabel} ${gstPercent}%` : `No ${taxLabel}`}</small>
                      {taxType ? (
                        <>
                          <Dot>•</Dot>
                          <small>{taxType}</small>
                        </>
                      ) : null}
                    </MetaLine>
                  </Left>
                  <StatusPill $applied={gstApplied}>
                    {gstApplied ? `${taxLabel} Applied` : `${taxLabel} Not Applied`}
                  </StatusPill>
                </Row>
              );
            })}
          </List>
        )}
      </Card>
    </Page>
  );
};

export default BulkGstUpdatePage;

const normalizeDisplayOrder = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : Number.MAX_SAFE_INTEGER;
};

const sortProducts = (list = []) =>
  [...list].sort((a, b) => {
    const orderA = normalizeDisplayOrder(a?.displayorder ?? a?.display_order);
    const orderB = normalizeDisplayOrder(b?.displayorder ?? b?.display_order);
    if (orderA !== orderB) return orderA - orderB;
    const nameA = (a?.name || "").toLowerCase();
    const nameB = (b?.name || "").toLowerCase();
    if (nameA !== nameB) return nameA.localeCompare(nameB);
    const idA = productIdentity(a);
    const idB = productIdentity(b);
    const numericA = Number(idA);
    const numericB = Number(idB);
    if (Number.isFinite(numericA) && Number.isFinite(numericB)) return numericA - numericB;
    return idA.localeCompare(idB, "en", { numeric: true });
  });

const pickGstPercent = (item = {}) => {
  const raw =
    item?.gstpercetage ??
    item?.gstpercentage ??
    item?.gst_percent ??
    item?.gstPercent ??
    item?.gst ??
    0;
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
};

const pickTaxTypeLabel = (item = {}) => {
  const raw = String(item?.withorwithoutgst ?? item?.tax_type ?? "")
    .toLowerCase()
    .trim();
  if (["with", "withgst", "withtax", "with_gst"].includes(raw)) return "With GST";
  if (["without", "withoutgst", "withouttax", "without_gst"].includes(raw)) {
    return "Without GST";
  }
  return "";
};

const isGstApplied = (item = {}) => {
  const percent = pickGstPercent(item);
  const gstEnabledRaw = item?.gstenable ?? item?.gst_enabled;
  if (gstEnabledRaw !== undefined && gstEnabledRaw !== null) {
    return Number(gstEnabledRaw) === 1 && percent > 0;
  }
  return percent > 0;
};

const pickCategoryLabel = (item = {}, categories = []) => {
  const inlineName =
    item?.category_name ??
    item?.productcategoryname ??
    item?.product_category_name ??
    item?.categoryname;
  if (inlineName) return String(inlineName);

  const categoryId = String(
    item?.productcategoryid ??
      item?.product_category_id ??
      item?.categoryid ??
      item?.category_id ??
      ""
  );
  if (!categoryId) return "Uncategorized";

  const match = (categories || []).find(
    (cat) => String(cat?.id ?? cat?.productcategoryid ?? "") === categoryId
  );
  return String(match?.productcategoryname ?? match?.name ?? "Uncategorized");
};

const Page = styled.div`
  min-height: 100vh;
  padding: 20px;
  background: #f4f7fb;
  display: grid;
  gap: 18px;
  align-content: start;

  @media (max-width: 640px) {
    padding: 12px;
    gap: 12px;
  }
`;

const Hero = styled.div`
  background: linear-gradient(135deg, rgba(var(--app-primary-rgb), 1), rgba(0, 57, 98, 1));
  color: #fff;
  border-radius: 16px;
  padding: 14px 16px;
  box-shadow: 0 10px 24px rgba(var(--app-primary-rgb), 0.18);
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.25);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-size: 12px;
`;

const Title = styled.h1`
  margin: 8px 0 4px;
  font-size: 1.6rem;
  font-weight: 900;
`;

const Sub = styled.div`
  opacity: 0.9;
`;

const Controls = styled.div`
  background: #fff;
  border: 1px solid #dfe7f1;
  border-radius: 16px;
  padding: 20px;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(180px, 0.75fr) minmax(240px, 1fr) minmax(140px, 0.45fr);
  align-items: end;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.05);

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    padding: 16px;
  }
`;

const ControlBlock = styled.div`
  display: grid;
  gap: 8px;
  min-width: 0;

  label {
    font-size: 14px;
    font-weight: 800;
    color: #0f172a;
  }

  select,
  input {
    width: 100%;
    height: 46px;
    box-sizing: border-box;
    padding: 0 14px;
    border-radius: 10px;
    border: 1px solid #cbd5e1;
    background: #f8fafc;
    color: #0f172a;
    font: inherit;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

    &:hover {
      border-color: #94a3b8;
    }

    &:focus {
      border-color: ${BRAND};
      background: #fff;
      box-shadow: 0 0 0 3px rgba(var(--app-primary-rgb), 0.12);
    }

    &::placeholder {
      color: #94a3b8;
    }
  }
`;

const PrimaryBtn = styled.button`
  background: linear-gradient(135deg, ${BRAND}, ${BRAND_DARK});
  color: #fff;
  border: 0;
  border-radius: 12px;
  min-height: 46px;
  padding: 0 20px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(var(--app-primary-rgb), 0.22);
  transition: transform 0.12s ease, box-shadow 0.16s ease, filter 0.16s ease;
  &:hover {
    filter: brightness(1.03);
    box-shadow: 0 12px 24px rgba(var(--app-primary-rgb), 0.28);
  }
  &:active {
    transform: translateY(1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }

  @media (max-width: 900px) {
    grid-column: 1 / -1;
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: #fff;
  border: 1px solid #dfe7f1;
  border-radius: 14px;
  padding: 12px 16px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
  button {
    border: 1px solid rgba(var(--app-primary-rgb), 0.28);
    background: ${BRAND_SOFT};
    color: ${BRAND};
    border-radius: 10px;
    padding: 7px 12px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s ease, transform 0.1s ease;
    &:hover {
      background: #d9ecfa;
    }
    &:active {
      transform: translateY(1px);
    }
  }

  @media (max-width: 640px) {
    align-items: stretch;

    > div:not(:first-child),
    > button {
      text-align: center;
    }
  }
`;

const FilterGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    width: 100%;

    button {
      padding-left: 6px;
      padding-right: 6px;
      font-size: 12px;
    }
  }
`;

const FilterBtn = styled.button`
  border: 2px solid ${(p) => (p.$active ? BRAND_DARK : "rgba(var(--app-primary-rgb), 0.28)")};
  background: ${(p) =>
    p.$active ? `linear-gradient(135deg, ${BRAND}, ${BRAND_DARK})` : BRAND_SOFT};
  color: ${(p) => (p.$active ? "#fff" : BRAND)};
  border-radius: 999px;
  padding: 7px 12px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: ${(p) =>
    p.$active ? "0 8px 18px rgba(var(--app-primary-rgb), 0.3)" : "0 0 0 rgba(0, 0, 0, 0)"};
  transform: ${(p) => (p.$active ? "translateY(-1px)" : "none")};
  transition: background 0.15s ease, transform 0.1s ease, box-shadow 0.15s ease;
  &:hover {
    filter: brightness(1.02);
  }
  &:active {
    transform: translateY(1px);
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.06);

  @media (max-width: 640px) {
    padding: 10px;
  }
`;

const Center = styled.div`
  text-align: center;
  color: #64748b;
  padding: 20px 0;
`;

const List = styled.div`
  display: grid;
  gap: 10px;
`;

const Row = styled.div`
  border: 1px solid ${(p) => (p.$selected ? "var(--app-primary)" : "#e5e7eb")};
  background: ${(p) => (p.$selected ? "#eef2ff" : "#f8fafc")};
  border-radius: 12px;
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  label {
    display: flex;
    gap: 8px;
    align-items: center;
    font-weight: 700;
  }

  @media (max-width: 640px) {
    align-items: flex-start;
    gap: 10px;
    padding: 12px;
  }
`;

const Left = styled.div`
  display: grid;
  gap: 4px;
`;

const MetaLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  small {
    color: #64748b;
    font-weight: 500;
  }
`;

const Dot = styled.span`
  color: #94a3b8;
  font-size: 12px;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid ${(p) => (p.$applied ? "#86efac" : "#cbd5e1")};
  color: ${(p) => (p.$applied ? "#166534" : "#475569")};
  background: ${(p) => (p.$applied ? "#f0fdf4" : "#f8fafc")};
`;
